import type {
  ActionAssociatedObjectsPayload,
  ActionPlan,
  ActionTodoItemPayload,
  ActionCreateResponse,
  ActionUpdateResponse,
  CreateActionPayload,
  UpdateActionPayload,
} from '@/contexts/actions/Actions.interfaces'
import {
  type RestAPIActionCreated,
  type RestAPIActionCriteria,
  type RestAPIActionUpdateAssociationsRequest,
  type RestAPIActionUpdateRequest,
  type RestActionAssociations,
  type RestActionMeasure,
  type RestAPIOwner,
  type RestAPISubAction,
  type RestApiActionUpdated,
  RestApiActionCriteriaStatus,
  RestApiActionCriteriaTemplateType,
  RestApiActionUpdateRequestStatus,
  RestApiOwnerType,
} from '@/interfaces/ApiRest.generated.interfaces'
import { ActionStatus } from '@/contexts/action-plans/ActionPlans.interfaces'
import { convertEnumValue } from '@/helpers/Enum.helpers'
import type { Owner } from '@/contexts/shared/owners/Owners.interfaces'
import type { TranslatedFields } from '@/interfaces/Language.interfaces'
import type { NullableId, SimpleId } from '@/interfaces/Shared.interfaces'

const mapTranslatedFields = (
  fields: TranslatedFields | null | undefined
): { en?: string | null; fr?: string | null } | null => (fields ? { en: fields.en, fr: fields.fr } : null)

const mapOwner = (owner: Owner | null | undefined): RestAPIOwner | null => {
  const type = convertEnumValue(owner?.type, RestApiOwnerType)

  return owner && type ? { type, id: owner.id } : null
}

const mapActionPlans = (plans: ActionPlan[] | undefined) =>
  plans?.map(({ id, name }) => ({ id, name: { fr: name, en: name } })) ?? []

const mapTodoItem = (item: ActionTodoItemPayload): RestAPISubAction => ({
  id: item.id,
  identifier: item.identifier,
  name: { en: item.names.en, fr: item.names.fr },
  description: mapTranslatedFields(item.descriptions),
  proof_required: item.proofRequired,
  subaction_position: item.position,
  cost: item.cost,
  workload: item.workload,
  spent_cost: item.spentCost,
  spent_workload: item.spentWorkload,
  progress: item.progress,
  done: item.done,
  assigned_to: mapOwner(item.owner),
  link: item.link,
})

const mapTodoItems = (items: ActionTodoItemPayload[] | null | undefined): RestAPISubAction[] | null =>
  items ? items.map(mapTodoItem) : null

const buildActionMeasures = (
  perimeterId: number,
  beneficiaryIds: number[],
  solutionId: number,
  impact?: number
): RestActionMeasure[] => {
  const measureImpact = impact ?? 0

  return [
    { security_measure_id: solutionId, perimeter_id: perimeterId, impact: measureImpact },
    ...beneficiaryIds.map((beneficiaryId) => ({
      security_measure_id: solutionId,
      perimeter_id: beneficiaryId,
      impact: measureImpact,
    })),
  ]
}

const mapCreateAssociatedObjects = (
  associatedObjects: ActionAssociatedObjectsPayload | null | undefined,
  measures: RestActionMeasure[]
): RestActionAssociations => ({
  measures,
  labels: associatedObjects?.labels ?? [],
  isp_projects: associatedObjects?.ispProjects ?? [],
  incidents: associatedObjects?.incidents ?? [],
  gaps: associatedObjects?.gaps ?? [],
  exemptions: associatedObjects?.exemptions ?? [],
  group_action:
    associatedObjects?.actionRegisterGroups?.map(({ id, name, identifier }) => ({ id, name, identifier })) ?? [],
})

const mapUpdateAssociatedObjects = (
  associatedObjects: ActionAssociatedObjectsPayload | null | undefined
): RestAPIActionUpdateAssociationsRequest | null =>
  associatedObjects
    ? {
        labels: associatedObjects.labels ?? [],
        isp_projects: associatedObjects.ispProjects ?? [],
        incidents: associatedObjects.incidents ?? [],
        gaps: associatedObjects.gaps ?? [],
        exemptions: associatedObjects.exemptions ?? [],
        group_action:
          associatedObjects.actionRegisterGroups?.map(({ id, name, identifier }) => ({ id, name, identifier })) ?? [],
      }
    : null

export const createActionToApi = (payload: CreateActionPayload): RestAPIActionCriteria => {
  const measures = payload.solutionId
    ? buildActionMeasures(payload.perimeterId, payload.beneficiaryIds ?? [], payload.solutionId, payload.impact)
    : []

  const result: RestAPIActionCriteria = {
    action_plans: mapActionPlans(payload.actionPlans),
    associated_objects: mapCreateAssociatedObjects(payload.associatedObjects, measures),
    assigned_to: mapOwner(payload.owner),
    category_id: payload.categoryId,
    completion: payload.completion,
    date_start: payload.dateStart,
    description: mapTranslatedFields(payload.descriptions),
    file_required: payload.fileRequired,
    forked_from_id: payload.forkedFromId,
    identifier: payload.identifier,
    initial_cost: payload.initialCost,
    init_workload: payload.initWorkload,
    is_template: payload.isTemplate,
    name: mapTranslatedFields(payload.names),
    perimeter_id: payload.perimeterId,
    priority: payload.priority,
    status: convertEnumValue(payload.status, RestApiActionCriteriaStatus) ?? RestApiActionCriteriaStatus.TO_PLAN,
    sub_action_items: mapTodoItems(payload.todoItems) ?? [],
    target_date: payload.targetDate,
  }

  if (payload.templateType) {
    result.template_type = convertEnumValue(payload.templateType, RestApiActionCriteriaTemplateType)
  }

  return result
}

export const updateActionToApi = (payload: UpdateActionPayload): RestAPIActionUpdateRequest => ({
  action_id: payload.actionId,
  action_plans: mapActionPlans(payload.actionPlans),
  assigned_to: mapOwner(payload.owner),
  associated_objects: mapUpdateAssociatedObjects(payload.associatedObjects),
  completion: payload.completion,
  consume_perimeters: payload.consumePerimeters?.map((id) => ({ id })) ?? null,
  current_efficiency: payload.currentEfficiency,
  date_start: payload.dateStart,
  description: mapTranslatedFields(payload.descriptions),
  file_required: payload.fileRequired,
  identifier: payload.identifier,
  initial_cost: payload.initialCost,
  init_workload: payload.initWorkload,
  isp_impacts: payload.ispImpacts?.map((impact) => ({ id: impact.id, target_date: impact.targetDate })) ?? null,
  link: payload.link,
  name: mapTranslatedFields(payload.names),
  perimeter_id: payload.perimeterId,
  priority: payload.priority,
  spent_cost: payload.spentCost,
  spent_workload: payload.spentWorkload,
  status: convertEnumValue(payload.status, RestApiActionUpdateRequestStatus),
  sub_action_items: mapTodoItems(payload.todoItems),
  target_date: payload.targetDate,
})

export const actionCreateResponseFromApi = (action: RestAPIActionCreated): ActionCreateResponse => {
  const actionPlan: SimpleId | NullableId = action.action_plan ? { id: action.action_plan.id } : { id: null }

  return {
    id: action.id,
    identifier: action.identifier,
    perimeter_id: action.perimeter_id,
    name: action.name,
    status: convertEnumValue(action.status, ActionStatus) ?? ActionStatus.TO_PLAN,
    action_plan: actionPlan,
    completion: action.completion,
    impact: action.impact ?? 0,
    owner: undefined,
  }
}

export const actionUpdateResponseFromApi = (action: RestApiActionUpdated): ActionUpdateResponse => ({
  id: action.id,
  identifier: action.identifier,
  status: convertEnumValue(action.status, ActionStatus) ?? ActionStatus.TO_PLAN,
  completion: action.completion,
})
